<script>
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { getApiUrl } from '../api';
  import { selectedCountry } from '../countryStore';
  import { t } from '../i18nStore';
  import {
    calculateOpenAccountReward,
    normalizeOpenAccountPromotion,
    getBankGroup
  } from '../services/openAccountCalculator';
  import OpenAccountShareCard from '../components/OpenAccountShareCard.svelte';

  let totalNewMoney = 3000000;
  let preference = 'cash'; // 'cash' | 'miles'
  let crossPromotion = true;

  let promotions = [];
  let selectedPromoIds = new Set();
  let isLoadingPromos = false;
  let calculationResult = null;

  onMount(async () => {
    await loadPromotions();
  });

  async function loadPromotions() {
    isLoadingPromos = true;
    try {
      const country = ($selectedCountry === 'Hong Kong' || $selectedCountry === 'HK') ? 'HK' : ($selectedCountry || 'HK');
      const url = getApiUrl(`/promotions/search?includeExpired=false&categories=open_account,Open+Account&countryCode=${country}&page=1&pageSize=50`);
      const res = await fetch(url);
      let items = [];
      if (res.ok) {
        const json = await res.json();
        items = json.data || [];
      }

      if (items.length > 0) {
        promotions = items.map(p => normalizeOpenAccountPromotion(p));
      } else {
        promotions = [];
      }
    } catch (e) {
      promotions = [];
    } finally {
      selectedPromoIds = new Set(promotions.map(p => p.id));
      isLoadingPromos = false;
    }
  }

  $: canCalculate = totalNewMoney > 0 && selectedPromoIds.size > 0 && !isLoadingPromos;

  $: sameBankWarnings = (() => {
    if (selectedPromoIds.size < 2) return [];
    const bankPromoMap = new Map();
    for (const id of selectedPromoIds) {
      const p = promotions.find(item => item.id === id);
      if (p) {
        const grp = getBankGroup(p);
        if (!bankPromoMap.has(grp)) bankPromoMap.set(grp, []);
        bankPromoMap.get(grp).push(p);
      }
    }
    const list = [];
    for (const [grp, pList] of bankPromoMap.entries()) {
      if (pList.length > 1) {
        list.push($t('open_account.same_bank_warning', { bank: grp }));
      }
    }
    return list;
  })();

  function togglePromo(id) {
    if (selectedPromoIds.has(id)) {
      selectedPromoIds.delete(id);
    } else {
      selectedPromoIds.add(id);
    }
    selectedPromoIds = new Set(selectedPromoIds);
  }

  function selectAllPromos() {
    selectedPromoIds = new Set(promotions.map(p => p.id));
  }

  function clearAllPromos() {
    selectedPromoIds = new Set();
  }

  function setPreset(amount) {
    totalNewMoney = amount;
  }

  function handleCalculate() {
    const selected = promotions.filter(p => selectedPromoIds.has(p.id));
    calculationResult = calculateOpenAccountReward({
      totalNewMoney: Number(totalNewMoney) || 0,
      selectedPromotions: selected,
      crossPromotion,
      preference
    });

    setTimeout(() => {
      const el = document.getElementById('result-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }

  function formatPromoSubtitle(promo) {
    if (promo.tiers && promo.tiers.length > 0) {
      const first = promo.tiers[0].fixedReward;
      const last = promo.tiers[promo.tiers.length - 1].fixedReward;
      const minSp = promo.minDeposit > 0 ? ` (Min HK$${promo.minDeposit.toLocaleString()})` : '';
      if (first === last) {
        return `${promo.brand} • Fixed HK$${first.toLocaleString()}${minSp}`;
      }
      return `${promo.brand} • Tiered: HK$${first.toLocaleString()} - HK$${last.toLocaleString()}${minSp}`;
    } else if (promo.isFixedReward && promo.fixedRewardAmount > 0) {
      return `${promo.brand} • Fixed HK$${promo.fixedRewardAmount.toLocaleString()} (Min HK$${promo.minDeposit.toLocaleString()})`;
    }
    return `${promo.brand} • ${promo.unit === 'miles' ? $t('open_account.pref_miles') : $t('open_account.pref_cash')}`;
  }
</script>

<div class="calculator-page">
  <div class="container">
    <!-- Banner Header -->
    <div class="banner">
      <div class="banner-badge">
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="3" y1="21" x2="21" y2="21"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
          <polygon points="12 2 2 7 22 7 12 2"></polygon>
          <line x1="7" y1="10" x2="7" y2="21"></line>
          <line x1="12" y1="10" x2="12" y2="21"></line>
          <line x1="17" y1="10" x2="17" y2="21"></line>
        </svg>
      </div>
      <div>
        <h1>{$t('open_account.banner_title')}</h1>
        <p>{$t('open_account.banner_subtitle')}</p>
      </div>
    </div>

    <!-- Section 1: Rebate Preference -->
    <div class="section-card">
      <div class="card-section-header">{$t('open_account.section1_title')}</div>
      <div class="preference-grid">
        <label class="pref-option {preference === 'cash' ? 'selected' : ''}">
          <input type="radio" name="preference" value="cash" bind:group={preference} />
          <div class="pref-content">
            <div class="pref-icon">💰</div>
            <div>
              <div class="pref-title">{$t('open_account.pref_cash')}</div>
              <div class="pref-desc">{$t('open_account.pref_cash_desc')}</div>
            </div>
          </div>
        </label>
        <label class="pref-option {preference === 'miles' ? 'selected' : ''}">
          <input type="radio" name="preference" value="miles" bind:group={preference} />
          <div class="pref-content">
            <div class="pref-icon">✈️</div>
            <div>
              <div class="pref-title">{$t('open_account.pref_miles')}</div>
              <div class="pref-desc">{$t('open_account.pref_miles_desc')}</div>
            </div>
          </div>
        </label>
      </div>
    </div>

    <!-- Section 2: Total New Money Amount -->
    <div class="section-card">
      <div class="card-section-header">{$t('open_account.section2_title')}</div>
      <div class="input-wrapper">
        <span class="currency-prefix">HK$</span>
        <input
          type="number"
          class="money-input"
          bind:value={totalNewMoney}
          placeholder="e.g. 3,000,000"
          min="0"
          step="10000"
        />
      </div>

      <!-- Preset Quick Chips -->
      <div class="preset-chips">
        <button type="button" class="chip {totalNewMoney === 500000 ? 'active' : ''}" on:click={() => setPreset(500000)}>
          HK$ 500k
        </button>
        <button type="button" class="chip {totalNewMoney === 1000000 ? 'active' : ''}" on:click={() => setPreset(1000000)}>
          HK$ 1M
        </button>
        <button type="button" class="chip {totalNewMoney === 2000000 ? 'active' : ''}" on:click={() => setPreset(2000000)}>
          HK$ 2M
        </button>
        <button type="button" class="chip {totalNewMoney === 3000000 ? 'active' : ''}" on:click={() => setPreset(3000000)}>
          HK$ 3M
        </button>
        <button type="button" class="chip {totalNewMoney === 5000000 ? 'active' : ''}" on:click={() => setPreset(5000000)}>
          HK$ 5M
        </button>
      </div>
    </div>

    <!-- Section 3: Open Account Promotions Selection -->
    <div class="section-card">
      <div class="promo-header-row">
        <div>
          <div class="card-section-header">{$t('open_account.section3_title')}</div>
          <div class="cross-promo-title">{$t('open_account.cross_promotion')}</div>
          <div class="cross-promo-desc">{$t('open_account.cross_promotion_desc')}</div>
        </div>
        <label class="switch">
          <input type="checkbox" bind:checked={crossPromotion} />
          <span class="slider"></span>
        </label>
      </div>

      <hr class="card-divider" />

      <!-- Count & Select/Clear -->
      <div class="selection-control-row">
        <div class="promo-count">
          {$t('open_account.promos_selected', { selected: selectedPromoIds.size, total: promotions.length })}
        </div>
        <div class="promo-actions">
          <button type="button" class="text-btn" on:click={selectAllPromos}>{$t('open_account.select_all')}</button>
          <span class="sep">|</span>
          <button type="button" class="text-btn" on:click={clearAllPromos}>{$t('open_account.clear')}</button>
        </div>
      </div>

      <!-- Same Bank Warnings -->
      {#if sameBankWarnings.length > 0}
        <div class="warnings-box">
          {#each sameBankWarnings as warn}
            <div class="warning-item">
              <span>⚠️</span>
              <span>{warn}</span>
            </div>
          {/each}
        </div>
      {/if}

      <!-- Promotions Checklist -->
      {#if isLoadingPromos}
        <div class="loading-state">{$t('open_account.loading_promos')}</div>
      {:else if promotions.length === 0}
        <div class="empty-state">
          <div class="empty-icon">ℹ️</div>
          <div class="empty-text">{$t('open_account.empty_promos_msg')}</div>
        </div>
      {:else}
        <div class="promo-checklist">
          {#each promotions as p}
            <label class="promo-item {selectedPromoIds.has(p.id) ? 'checked' : ''}">
              <input
                type="checkbox"
                checked={selectedPromoIds.has(p.id)}
                on:change={() => togglePromo(p.id)}
              />
              <div class="promo-item-info">
                <div class="promo-item-name">{p.name}</div>
                <div class="promo-item-sub">{formatPromoSubtitle(p)}</div>
              </div>
            </label>
          {/each}
        </div>
      {/if}

      <div class="add-promo-footer">
        <button type="button" class="outline-btn" on:click={() => navigate('/add')}>
          {$t('open_account.add_promo_btn')}
        </button>
      </div>
    </div>

    <!-- Calculate Button -->
    <div class="calc-action-row">
      <button
        class="calc-button"
        disabled={!canCalculate}
        on:click={handleCalculate}
      >
        {$t('open_account.calculate_btn')}
      </button>
    </div>

    <!-- Results Section -->
    {#if calculationResult}
      <div id="result-section" class="result-container">
        <OpenAccountShareCard result={calculationResult} />
      </div>
    {/if}
  </div>
</div>

<style>
  .calculator-page {
    max-width: 1200px;
    margin: 0 auto;
    padding: 1.5rem 1rem 3rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  .container {
    width: 100%;
    margin: 0 auto;
  }

  .empty-state {
    text-align: center;
    padding: 32px 16px;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 12px;
    color: #475569;
    font-size: 14px;
    font-weight: 500;
    margin: 10px 0;
  }

  .empty-icon {
    font-size: 28px;
    margin-bottom: 8px;
  }

  .banner {
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    border-radius: 16px;
    padding: 24px;
    color: #ffffff;
    display: flex;
    align-items: center;
    gap: 18px;
    margin-bottom: 24px;
    box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.1);
  }

  .banner-badge {
    background: rgba(246, 200, 69, 0.15);
    color: #f6c845;
    padding: 14px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .banner h1 {
    font-size: 20px;
    font-weight: 800;
    margin: 0 0 6px 0;
  }

  .banner p {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
  }

  .section-card {
    background: #ffffff;
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  }

  .card-section-header {
    font-size: 11px;
    font-weight: 800;
    color: #d97706;
    letter-spacing: 0.8px;
    margin-bottom: 14px;
  }

  .preference-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  .pref-option {
    border: 1px solid #cbd5e1;
    border-radius: 12px;
    padding: 14px;
    cursor: pointer;
    background: #f8fafc;
    transition: all 0.2s;
  }

  .pref-option input {
    display: none;
  }

  .pref-option.selected {
    border-color: #0f172a;
    background: rgba(15, 23, 42, 0.04);
    box-shadow: 0 0 0 1px #0f172a;
  }

  .pref-content {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .pref-icon {
    font-size: 22px;
  }

  .pref-title {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
  }

  .pref-desc {
    font-size: 11px;
    color: #64748b;
    margin-top: 2px;
  }

  .input-wrapper {
    display: flex;
    align-items: center;
    border: 1.5px solid #cbd5e1;
    border-radius: 10px;
    padding: 8px 14px;
    background: #ffffff;
    transition: border-color 0.2s;
  }

  .input-wrapper:focus-within {
    border-color: #0f172a;
  }

  .currency-prefix {
    font-size: 18px;
    font-weight: 800;
    color: #0f172a;
    margin-right: 8px;
  }

  .money-input {
    border: none;
    outline: none;
    font-size: 20px;
    font-weight: 800;
    color: #0f172a;
    width: 100%;
  }

  .preset-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
  }

  .chip {
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    cursor: pointer;
    transition: all 0.2s;
  }

  .chip.active, .chip:hover {
    background: #0f172a;
    color: #ffffff;
    border-color: #0f172a;
  }

  .promo-header-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .cross-promo-title {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
  }

  .cross-promo-desc {
    font-size: 11px;
    color: #64748b;
    margin-top: 2px;
  }

  .card-divider {
    border: none;
    border-top: 1px solid #e2e8f0;
    margin: 14px 0;
  }

  .selection-control-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .promo-count {
    font-size: 12px;
    font-weight: 700;
    color: #0f172a;
  }

  .promo-actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .text-btn {
    background: none;
    border: none;
    color: #0f172a;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
  }

  .text-btn:hover {
    text-decoration: underline;
  }

  .sep {
    color: #cbd5e1;
    font-size: 12px;
  }

  .warnings-box {
    margin-bottom: 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .warning-item {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 11px;
    color: #92400e;
  }

  .promo-checklist {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .promo-item {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 10px 14px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .promo-item.checked {
    border-color: rgba(15, 23, 42, 0.4);
    background: rgba(15, 23, 42, 0.02);
  }

  .promo-item-info {
    flex: 1;
  }

  .promo-item-name {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
  }

  .promo-item-sub {
    font-size: 11px;
    color: #64748b;
    margin-top: 2px;
  }

  .add-promo-footer {
    margin-top: 14px;
  }

  .outline-btn {
    background: #ffffff;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    padding: 8px 14px;
    font-size: 12px;
    font-weight: 600;
    color: #0f172a;
    cursor: pointer;
    width: 100%;
    transition: border-color 0.2s;
  }

  .outline-btn:hover {
    border-color: #0f172a;
  }

  .calc-action-row {
    margin: 24px 0;
  }

  .calc-button {
    width: 100%;
    background: #0f172a;
    color: #ffffff;
    border: none;
    border-radius: 12px;
    padding: 16px;
    font-size: 16px;
    font-weight: 800;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
    transition: background 0.2s;
  }

  .calc-button:hover:not(:disabled) {
    background: #1e293b;
  }

  .calc-button:disabled {
    background: #94a3b8;
    cursor: not-allowed;
    box-shadow: none;
  }

  .result-container {
    margin-top: 20px;
  }

  .loading-state {
    text-align: center;
    padding: 20px;
    color: #64748b;
    font-size: 13px;
  }

  /* Toggle Switch */
  .switch {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
  }

  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #cbd5e1;
    transition: 0.3s;
    border-radius: 24px;
  }

  .slider:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }

  input:checked + .slider {
    background-color: #0f172a;
  }

  input:checked + .slider:before {
    transform: translateX(20px);
  }

  @media (max-width: 640px) {
    .preference-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
